import { Link, NavLink, useNavigate } from 'react-router'
import { Button } from '@/components/ui/Button'
import { selectPublicAuthStatus, useAuthStore } from '@/stores/authStore'
import { useAdminUi } from '@/hooks/useAdminUi'
import { UsageBadge } from '@/components/usage/UsageBadge'
import { isMakerDirectoryEnabled } from '@/lib/makers'

interface NavbarProps {
  variant?: 'landing' | 'app'
}

function Logo() {
  return (
    <Link to="/" className="font-[GeistPixel] text-lg tracking-tight text-black">
      {/* GeistPixel wordmark — full name on >=sm, abbreviated below. */}
      <span className="hidden sm:inline">Terrain&nbsp;6</span>
      <span className="sm:hidden">T6</span>
    </Link>
  )
}

function LandingNav() {
  const isAuthenticated = useAuthStore(selectPublicAuthStatus)

  return (
    <>
      <nav className="hidden items-center gap-6 md:flex">
        <a href="#about" className="text-sm text-gray-600 transition-colors hover:text-black">
          About
        </a>
        <a
          href="#how-it-works"
          className="text-sm text-gray-600 transition-colors hover:text-black"
        >
          How It Works
        </a>
        {/* A real route link, not an anchor: the #about / #how-it-works anchors
            above only resolve on "/" — /pricing needs to be reachable from
            anywhere this navbar renders. */}
        <Link to="/pricing" className="text-sm text-gray-600 transition-colors hover:text-black">
          Pricing
        </Link>
        {/* Quiet side door for visitors who want a physical print, not an
            account — sends them to the public maker directory. */}
        {isMakerDirectoryEnabled() && (
          <Link
            to="/makers"
            onClick={() => window.t6track?.('landing_cta_clicked', { source: 'nav_find_a_maker' })}
            className="text-sm text-gray-600 transition-colors hover:text-black"
          >
            Find a Maker
          </Link>
        )}
      </nav>
      {isAuthenticated === null ? (
        <span
          aria-label="Checking session"
          role="status"
          className="h-11 w-32 animate-pulse rounded-lg bg-gray-100"
        />
      ) : isAuthenticated ? (
        <Button variant="primary" to="/app" className="text-sm">
          Go to App
        </Button>
      ) : (
        <>
          <Link
            to="/auth/login"
            className="hidden text-sm text-gray-600 transition-colors hover:text-black sm:inline"
          >
            Sign in
          </Link>
          {/* Subscribing is the primary path in: the persistent nav CTA points at
              the pricing table, not straight into a free-trial signup. */}
          <Button variant="primary" to="/pricing" className="text-sm">
            Get Started
          </Button>
        </>
      )}
    </>
  )
}

function AppNav() {
  const logout = useAuthStore((s) => s.logout)
  const showAdminUi = useAdminUi()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `inline-block border-b-2 pb-[2px] -mb-[2px] text-sm transition-colors ${
      isActive
        ? 'border-sage font-medium text-black'
        : 'border-transparent text-gray-600 hover:text-black'
    }`

  return (
    <>
      <nav className="hidden items-center gap-6 md:flex">
        <NavLink to="/app" end className={navLinkClass}>
          Home
        </NavLink>
        <NavLink to="/app/explore" className={navLinkClass}>
          Explore
        </NavLink>
        <NavLink to="/app/account" className={navLinkClass}>
          Account
        </NavLink>
        {showAdminUi && (
          <NavLink to="/app/admin" className={navLinkClass}>
            Admin
          </NavLink>
        )}
      </nav>
      <UsageBadge />
      <Button variant="ghost" onClick={handleLogout} className="text-sm">
        Log Out
      </Button>
    </>
  )
}

export function Navbar({ variant = 'landing' }: NavbarProps) {
  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-100 bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
        <Logo />
        <div className="flex items-center gap-4">
          {variant === 'landing' ? <LandingNav /> : <AppNav />}
        </div>
      </div>
    </header>
  )
}
